// src/modules/operaciones/inventarios/components/ModalDetallePrecio.js
//
// Modal con la existencia por bodega de un artículo.
import React from 'react'; // Importa React para usar JSX
import { fmt } from '../../../../utils/format'; // Formatea números (separador de miles)
import { useModalA11y } from '../../../../hooks/useModalA11y'; // Hook de accesibilidad para modales
import { colorSaldo } from '../utils/preciosComun'; // Color del saldo
import { BadgeTipo, Spinner } from './ComponentesPrecios'; // Badge del tipo de alerta e indicador de carga

/* ---------- Modal de detalle (accesibilidad en useModalA11y) ---------- */
// Muestra la existencia por bodega de un artículo. Props: articulo = artículo con su estado de carga
// (cargandoDetalle, errorDetalle, bodegas), tema = paleta de clases, onClose = cierra el modal
export default function ModalDetalle({ articulo, tema, onClose }) {
  const { modalRef, inicialRef } = useModalA11y(onClose); // Manejo de foco, Esc y scroll lock

  return (
    // Fondo oscuro: un clic fuera del diálogo lo cierra
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Detalle de ${articulo.NOMBRE}`}
        className={`${tema.bgCard} border ${tema.borderMain} rounded-3xl p-6 max-w-2xl w-full max-h-[85vh] shadow-2xl flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Encabezado: tipo de alerta, nombre, código y botón de cierre */}
        <div className="flex justify-between items-start mb-4 gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <BadgeTipo tipoAlerta={articulo.TIPO_ALERTA} claseSub={tema.textSub} />
            </div>
            <h2 className={`text-lg font-extrabold ${tema.textMain} break-words`}>{articulo.NOMBRE}</h2>
            <p className={`text-sm ${tema.textSub} font-mono`}>Código: {articulo.CODIGO}</p>
          </div>
          <button
            onClick={onClose}
            className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-full ${tema.bgBtn} ${tema.textMain} border ${tema.borderInput} hover:opacity-80 cursor-pointer`}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <p className={`text-xs uppercase tracking-wider font-bold ${tema.textSub} mb-2`}>Existencia por Bodega</p>

        {/* Contenido: cargando → error → vacío → tarjetas por bodega */}
        <div className="flex-1 overflow-y-auto min-h-0 pr-1">
          {articulo.cargandoDetalle ? (
            <div className="py-8 flex flex-col items-center gap-3">
              <Spinner />
              <p className={`text-sm ${tema.textSub}`}>Cargando…</p>
            </div>
          ) : articulo.errorDetalle ? (
            <p role="alert" aria-live="assertive" className="text-sm text-red-500 py-6 text-center">Error: {articulo.errorDetalle}</p>
          ) : articulo.bodegas.length === 0 ? (
            <p className={`text-sm ${tema.textSub} py-6 text-center`}>Sin existencias registradas.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {articulo.bodegas.map((b) => (
                // Las bodegas con saldo 0 se atenúan
                <div
                  key={b.BODE_COD}
                  className={`${tema.bgTarjeta} border ${tema.borderMain} rounded-2xl p-4 flex flex-col gap-2 ${Number(b.EXIS_SALDO) === 0 ? 'opacity-60' : ''}`}
                >
                  <div className="min-w-0">
                    <p className={`text-sm font-bold ${tema.textMain} truncate`} title={b.BODE_NOM}>{b.BODE_NOM || 'Sin nombre'}</p>
                    <p className={`text-xs font-mono ${tema.textSub}`}>Bodega: {b.BODE_COD}</p>
                  </div>
                  <div className="flex items-end justify-between">
                    <span className={`text-3xl font-extrabold font-mono leading-none ${colorSaldo(Number(b.EXIS_SALDO))}`}>{fmt(b.EXIS_SALDO)}</span>
                    {Number(b.EXIS_RESERVA) > 0 && <span className={`text-xs ${tema.textSub}`}>Reservado: {fmt(b.EXIS_RESERVA)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pie: existencia total del artículo */}
        <div className={`flex justify-between items-center mt-4 pt-4 border-t ${tema.borderMain}`}>
          <span className={`text-sm font-bold ${tema.textSub}`}>Existencia Total:</span>
          <span className={`text-xl font-extrabold font-mono ${colorSaldo(Number(articulo.EXISTENCIA_TOTAL))}`}>{fmt(articulo.EXISTENCIA_TOTAL)}</span>
        </div>

        <button
          ref={inicialRef}
          onClick={onClose}
          className="w-full mt-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-colors cursor-pointer"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}